import type { Dispatch, SetStateAction } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@packages/fenrir/components';

import type { ActionConfirmationProps } from './grid-action-toolbar';

export type ConfirmActionDialogProps = {
  confirmAction: ActionConfirmationProps | null;
  setConfirmAction: Dispatch<SetStateAction<ActionConfirmationProps | null>>;
};

export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  confirmAction,
  setConfirmAction,
}) => {
  const { t } = useTranslation('ui');

  const handleDialogConfirm = () => {
    confirmAction?.onSuccess?.();
    setConfirmAction(null);
  };

  const handleDialogCancel = () => {
    confirmAction?.onCancel?.();
    setConfirmAction(null);
  };

  return (
    <Dialog
      open
      onClose={handleDialogCancel}
      aria-labelledby="grid-action-confirm-dialog-title"
      aria-describedby="grid-action-confirm-dialog-description"
    >
      <DialogTitle id="grid-action-confirm-dialog-title">
        {confirmAction?.title || t('saveChanges')}
      </DialogTitle>
      <DialogContent id="grid-action-confirm-dialog-description">
        <DialogContentText>
          {confirmAction?.description || t('saveChangesConfirmation')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogCancel}>
          {confirmAction?.cancelText || t('button.cancel')}
        </Button>
        <Button
          variant="contained"
          color={confirmAction?.isDestructive ? 'error' : 'primary'}
          onClick={handleDialogConfirm}
          autoFocus
        >
          {confirmAction?.actionText || t('button.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
